import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  formatEdgeFunctionInvokeError,
  formatUnknownError,
  getUserAccessTokenForFunctions,
  supabase,
} from '../lib/supabase';
import { ChatSidebar } from '../components/ChatSidebar';
import { ChatInterface } from '../components/ChatInterface';
import {
  PromptPanel,
  type ChatFunctionRequestPayload,
  type RebranchChatPayload,
  type SendMessageOptions,
} from '../components/PromptPanel';
import { RenameModal } from '../components/RenameModal';
import { DeleteModal } from '../components/DeleteModal';
import { normalizeMessageRagSources } from '../lib/messages';
import type { Chat, Message, RagSource } from '../types/chat';
import type { User } from '@supabase/supabase-js';

interface ChatPageProps {
  user: User;
}

/** PostgREST rejects inserts when a JSON key maps to a column that is not in the DB yet. */
function isMissingColumnError(
  error: { code?: string; message?: string } | null,
  columnName: string,
): boolean {
  if (!error) return false;
  if (error.code === 'PGRST204' || error.code === '42703') return true;
  const msg = String(error.message ?? '');
  return (
    msg.includes(columnName) ||
    msg.includes('schema cache') ||
    msg.includes('does not exist')
  );
}

const CHAT_SIDEBAR_OPEN_KEY = 'sop-ai:chat-sidebar-open';

function getInitialChatSidebarOpen(): boolean {
  try {
    const v = localStorage.getItem(CHAT_SIDEBAR_OPEN_KEY);
    if (v === 'false') return false;
    if (v === 'true') return true;
  } catch {
    /* private mode / quota */
  }
  return true;
}

export function ChatPage({ user }: ChatPageProps) {
  const { chatId: routeChatId } = useParams<{ chatId: string }>();
  const navigate = useNavigate();

  const [chats, setChats] = useState<Chat[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(getInitialChatSidebarOpen);
  const [renamingChatId, setRenamingChatId] = useState<string | null>(null);
  const [deletingChatId, setDeletingChatId] = useState<string | null>(null);
  const [chatsHydrated, setChatsHydrated] = useState(false);
  const [lastChatInvokePayload, setLastChatInvokePayload] = useState<ChatFunctionRequestPayload | null>(
    null,
  );

  const currentChatId = useMemo(() => {
    if (!routeChatId || !chats.length) return null;
    return chats.some((c) => c.id === routeChatId) ? routeChatId : null;
  }, [routeChatId, chats]);

  useEffect(() => {
    loadChats();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(CHAT_SIDEBAR_OPEN_KEY, String(sidebarOpen));
    } catch {
      /* ignore */
    }
  }, [sidebarOpen]);

  useEffect(() => {
    if (!chatsHydrated) return;

    if (chats.length === 0) {
      if (routeChatId) {
        navigate('/chat', { replace: true });
      }
      return;
    }
    const exists = routeChatId && chats.some((c) => c.id === routeChatId);
    if (!routeChatId || !exists) {
      navigate(`/chat/${chats[0].id}`, { replace: true });
    }
  }, [chatsHydrated, chats, routeChatId, navigate]);

  useEffect(() => {
    if (currentChatId) {
      loadMessages(currentChatId);
    } else {
      setMessages([]);
    }
    setLastChatInvokePayload(null);
  }, [currentChatId]);

  const loadChats = async () => {
    try {
      const { data } = await supabase
        .from('chats')
        .select('*')
        .order('updated_at', { ascending: false });

      if (data) {
        setChats(data);
      }
    } finally {
      setChatsHydrated(true);
    }
  };

  const loadMessages = async (chatId: string) => {
    const { data } = await supabase
      .from('messages')
      .select('*')
      .eq('chat_id', chatId)
      .order('created_at', { ascending: true });

    if (data) {
      setMessages(data.map((row) => normalizeMessageRagSources(row as Message)));
    }
  };

  const createNewChat = async (): Promise<string | null> => {
    const { data, error } = await supabase
      .from('chats')
      .insert({
        user_id: user.id,
        title: 'New Chat',
      })
      .select()
      .single();

    if (data && !error) {
      setChats((prev) => [data, ...prev]);
      navigate(`/chat/${data.id}`);
      return data.id;
    }
    return null;
  };

  const handleNewChat = async () => {
    await createNewChat();
  };

  const handleDeleteChat = (chatId: string) => {
    setDeletingChatId(chatId);
  };

  const confirmDeleteChat = async (chatId: string) => {
    await supabase.from('chats').delete().eq('id', chatId);

    const remainingChats = chats.filter((c) => c.id !== chatId);
    setChats(remainingChats);

    if (routeChatId === chatId) {
      if (remainingChats.length > 0) {
        navigate(`/chat/${remainingChats[0].id}`, { replace: true });
      } else {
        navigate('/chat', { replace: true });
      }
    }
  };

  const handleRenameChat = (chatId: string) => {
    setRenamingChatId(chatId);
  };

  const confirmRenameChat = async (chatId: string, newTitle: string) => {
    await supabase.from('chats').update({ title: newTitle }).eq('id', chatId);
    setChats(chats.map((c) => (c.id === chatId ? { ...c, title: newTitle } : c)));
  };

  const handleDeleteMessage = async (messageId: string) => {
    const { error } = await supabase.from('messages').delete().eq('id', messageId);
    if (!error) {
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
    } else {
      console.error('Failed to delete message:', error);
    }
  };

  const handleRebranchChat = async (payload: RebranchChatPayload) => {
    if (!currentChatId) {
      throw new Error('No active chat to rebranch');
    }

    const { data: chatRow, error: chatErr } = await supabase
      .from('chats')
      .insert({ user_id: user.id, title: payload.title })
      .select()
      .single();

    if (chatErr || !chatRow) {
      console.error('Rebranch chat failed:', chatErr);
      throw new Error(chatErr ? formatUnknownError(chatErr) : 'Could not create chat');
    }

    const { error: ctxErr } = await supabase.from('chat_context').insert({
      chat_id: chatRow.id,
      markdown_file_ids: payload.markdown_file_ids,
      tag_ids: [] as string[],
      kb_tags: payload.kb_tags,
      jurisdiction: payload.jurisdiction,
      state: payload.state,
      company_name: payload.company_name,
      company_ein: payload.company_ein,
      updated_at: new Date().toISOString(),
    });

    if (ctxErr) {
      console.error('Rebranch context failed:', ctxErr);
      await supabase.from('chats').delete().eq('id', chatRow.id);
      throw new Error(formatUnknownError(ctxErr) || 'Could not save context');
    }

    const messageRows = messages.map((m) => {
      const row: {
        chat_id: string;
        role: 'user' | 'assistant';
        content: string;
        created_at: string;
        rag_sources?: RagSource[] | null;
      } = {
        chat_id: chatRow.id,
        role: m.role,
        content: m.content,
        created_at: m.created_at,
      };
      if (m.rag_sources != null) {
        row.rag_sources = m.rag_sources;
      }
      return row;
    });

    if (messageRows.length > 0) {
      let { error: msgErr } = await supabase.from('messages').insert(messageRows);
      if (
        msgErr &&
        isMissingColumnError(msgErr, 'rag_sources') &&
        messageRows.some((r) => r.rag_sources != null)
      ) {
        const withoutRag = messageRows.map(({ rag_sources, ...rest }) => {
          void rag_sources;
          return rest;
        });
        const retry = await supabase.from('messages').insert(withoutRag);
        msgErr = retry.error;
      }
      if (msgErr) {
        console.error('Rebranch messages failed:', msgErr);
        await supabase.from('chats').delete().eq('id', chatRow.id);
        throw new Error(formatUnknownError(msgErr) || 'Could not copy messages');
      }
    }

    await supabase
      .from('chats')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', chatRow.id);

    setChats((prev) => [chatRow, ...prev]);
    navigate(`/chat/${chatRow.id}`, { state: { rebranchDraft: payload.draft } });
  };

  const handleSendMessage = async (content: string, options: SendMessageOptions) => {
    let activeChatId = currentChatId;
    if (!activeChatId) {
      const newId = await createNewChat();
      if (!newId) return;
      activeChatId = newId;
    }

    setIsLoading(true);

    const userMessage = {
      chat_id: activeChatId,
      role: 'user' as const,
      content,
    };

    const { data: userMsgData } = await supabase
      .from('messages')
      .insert(userMessage)
      .select()
      .single();

    if (userMsgData) {
      setMessages([...messages, userMsgData]);

      const chat = chats.find((c) => c.id === activeChatId);
      if (chat && chat.title === 'New Chat') {
        const newTitle = content.slice(0, 50) + (content.length > 50 ? '...' : '');
        await supabase
          .from('chats')
          .update({ title: newTitle })
          .eq('id', activeChatId);
        setChats((prev) =>
          prev.map((c) => (c.id === activeChatId ? { ...c, title: newTitle } : c)),
        );
      }

      await supabase
        .from('chats')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', activeChatId);

      try {
        const conversationHistory = [...messages, userMsgData].map((msg) => ({
          role: msg.role,
          content: msg.content,
        }));

        const markdownFileIds =
          options.markdownFileIds && options.markdownFileIds.length > 0
            ? options.markdownFileIds
            : null;

        const invokeBody: ChatFunctionRequestPayload = {
          messages: conversationHistory,
          markdownFileIds,
          jurisdiction: options.jurisdiction,
          state: options.state,
          kbTags: options.kbTags,
          companyName: options.companyName,
          companyEin: options.companyEin,
        };
        setLastChatInvokePayload(invokeBody);

        const accessToken = await getUserAccessTokenForFunctions();

        const { data: invokeData, error: invokeError, response: fnResponse } =
          await supabase.functions.invoke<{
            message?: string;
            error?: string;
            rag_sources?: RagSource[];
          }>('chat', {
            body: invokeBody,
            headers: { Authorization: `Bearer ${accessToken}` },
          });

        if (invokeError) {
          const detail = await formatEdgeFunctionInvokeError(invokeError, fnResponse);
          throw new Error(detail || 'Failed to get response from AI');
        }

        if (invokeData && typeof invokeData === 'object' && 'error' in invokeData && invokeData.error) {
          throw new Error(String(invokeData.error));
        }

        const aiResponse = invokeData?.message;
        if (aiResponse === undefined || aiResponse === '') {
          throw new Error('Invalid response from AI');
        }

        const ragSources = invokeData?.rag_sources;
        const baseAssistant = {
          chat_id: activeChatId,
          role: 'assistant' as const,
          content: aiResponse,
        };
        const withSources =
          Array.isArray(ragSources) && ragSources.length > 0
            ? { ...baseAssistant, rag_sources: ragSources as RagSource[] }
            : baseAssistant;

        let { data: assistantMsgData, error: assistantInsertError } = await supabase
          .from('messages')
          .insert(withSources)
          .select()
          .single();

        if (
          assistantInsertError &&
          'rag_sources' in withSources &&
          isMissingColumnError(assistantInsertError, 'rag_sources')
        ) {
          console.warn(
            'messages.rag_sources column missing; run migration 20260405000000_messages_rag_sources.sql. Saving reply without stored sources.',
          );
          const retry = await supabase.from('messages').insert(baseAssistant).select().single();
          assistantMsgData = retry.data;
          assistantInsertError = retry.error;
        }

        if (assistantInsertError) {
          throw assistantInsertError;
        }

        if (assistantMsgData) {
          let row = normalizeMessageRagSources(assistantMsgData as Message);
          if (Array.isArray(ragSources) && ragSources.length > 0) {
            row = { ...row, rag_sources: ragSources };
          }
          setMessages((prev) => [...prev, row]);
        }
      } catch (error) {
        console.error('Error getting AI response:', error);
        const explanation = formatUnknownError(error);
        const errorMessage = {
          chat_id: activeChatId,
          role: 'assistant' as const,
          content: `Sorry, something went wrong: ${explanation}`,
        };

        const { data: errorMsgData } = await supabase
          .from('messages')
          .insert(errorMessage)
          .select()
          .single();

        if (errorMsgData) {
          setMessages((prev) => [...prev, errorMsgData]);
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const renamingChat = chats.find((c) => c.id === renamingChatId);
  const deletingChat = chats.find((c) => c.id === deletingChatId);

  return (
    <div className="flex h-screen min-h-0 bg-white dark:bg-slate-950">
      <ChatSidebar
        chats={chats}
        currentChatId={currentChatId}
        onNewChat={handleNewChat}
        onSelectChat={(id) => navigate(`/chat/${id}`)}
        onDeleteChat={handleDeleteChat}
        onRenameChat={handleRenameChat}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />
      <PromptPanel
        chatId={currentChatId}
        activeChatTitle={currentChatId ? chats.find((c) => c.id === currentChatId)?.title : null}
        onSendMessage={handleSendMessage}
        onRebranchChat={handleRebranchChat}
        isLoading={isLoading}
        conversationMessageCount={messages.length}
        lastInvokePayload={lastChatInvokePayload}
      />
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <ChatInterface messages={messages} onDeleteMessage={handleDeleteMessage} />
      </div>
      {renamingChat && (
        <RenameModal
          currentTitle={renamingChat.title}
          onRename={(newTitle) => confirmRenameChat(renamingChat.id, newTitle)}
          onClose={() => setRenamingChatId(null)}
        />
      )}
      {deletingChat && (
        <DeleteModal
          chatTitle={deletingChat.title}
          onDelete={() => confirmDeleteChat(deletingChat.id)}
          onClose={() => setDeletingChatId(null)}
        />
      )}
    </div>
  );
}
